import {
  Dialog,
  TextField,
  DialogFooter,
  PrimaryButton,
  DefaultButton,
  ITextField,
  IDetailsList,
  DialogType,
  IColumn,
} from '@fluentui/react';
import { MutableRefObject, FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export interface IDetailListHeaderProps {
  isDialogHidden: boolean;
  hideDialog: () => void;
  detailsListRef: MutableRefObject<IDetailsList | null> | null;
  columnToEdit: MutableRefObject<IColumn | null> | null;
}
const dialogStyles = { main: { maxWidth: 450 } };
const modalProps = {
  titleAriaId: 'Dialog',
  subtitleAriaId: 'Dialog sub',
  isBlocking: false,
  styles: dialogStyles,
};

/**
 * Dialog popup to address accessibility issues with resizing the columns in a details list
 * See https://msazure.visualstudio.com/One/_workitems/edit/7339491 for reference
 */
export const DetailsListHeaderAccessibleResizeDialog = ({
  isDialogHidden,
  hideDialog,
  detailsListRef,
  columnToEdit,
}: IDetailListHeaderProps) => {
  const [t] = useTranslation();
  const textfieldRef = useRef<ITextField>(null);
  const input = useRef<number | null>(null);

  const resizeDialogContentProps = {
    type: DialogType.normal,
    title: t('ResizeColumn'),
    closeButtonAriaLabel: t('Close'),
    subText: t('EnterDesiredColumnWidth'),
  };

  const confirmDialog = () => {
    const detailsList = detailsListRef?.current;

    if (textfieldRef.current) {
      input.current = Number(textfieldRef.current.value);
    }

    if (columnToEdit?.current && input.current && detailsList) {
      const width = input.current;
      detailsList.updateColumn(columnToEdit.current, { width: width });
    }

    input.current = null;
    hideDialog();
  };

  return (
    <Dialog
      hidden={isDialogHidden}
      onDismiss={hideDialog}
      dialogContentProps={resizeDialogContentProps}
      modalProps={modalProps}
    >
      <TextField
        componentRef={textfieldRef}
        ariaLabel={t('EnterColumnWidth')}
        onKeyDown={ev => {
          if (ev.key === 'Enter') {
            confirmDialog();
            ev.preventDefault();
          }
        }}
      />
      <DialogFooter>
        <PrimaryButton onClick={confirmDialog} text={t('Resize')} />
        <DefaultButton onClick={hideDialog} text={t('Cancel')} />
      </DialogFooter>
    </Dialog>
  );
};
